import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { getListStores } from "../../../redux/actions/storeActions";
import { getListSections } from "../../../redux/actions/sectionActions";
import {
  deleteProduct,
  getListProducts,
} from "../../../redux/actions/productActions";
import DefaultLayout from "../../../layouts/DefaultLayout";
import ConfirmationModal from "../../../components/ConfirmationModal";
import Paginate from "../../../components/Paginate";
import { baseURLFile } from "../../../constants";
import Loader from "../../../components/Loader";
import Alert from "../../../components/Alert";

function ProductScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [productId, setProductId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listStores = useSelector((state) => state.getListStores);
  const { stores, loadingListStores, errorListStores } = listStores;

  const listProducts = useSelector((state) => state.getListProduct);
  const { products, loadingListProduct, errorListProduct, pages } =
    listProducts;

  const productDelete = useSelector((state) => state.deleteProduct);
  const { loadingProductDelete, errorProductDelete, successProductDelete } =
    productDelete;

  //
  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListStores("0"));
      dispatch(getListSections("0"));
      dispatch(getListProducts(page));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successProductDelete) {
      dispatch(getListStores("0"));
      dispatch(getListSections("0"));
      dispatch(getListProducts("1"));
    }
  }, [successProductDelete]);

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <a
                  href="/stores"
                  className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200"
                >
                  Stores
                </a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Products
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with Store Image */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12 flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Products Management
              </h1>
              <p className="text-white/80 max-w-xl">
                Manage your restaurant or grocery store products with all details and extras.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <a
                href="/products/new-product"
                className="inline-flex items-center px-5 py-3 bg-white text-primary hover:bg-gray-100 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-4 h-4 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                Add New Product
              </a>
            </div>
          </div>
        </div>
        {/* Modern Table Card */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            {loadingListProduct ? (
              <div className="flex justify-center items-center h-80">
                <Loader />
              </div>
            ) : errorListProduct ? (
              <div className="flex justify-center items-center h-80">
                <Alert message={errorListProduct} />
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-2 text-left">
                      <th className="min-w-[80px] py-4 px-4 font-semibold text-black text-sm rounded-l-lg">
                        #
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm">
                        Image
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Product Name
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Section
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Store
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Type
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {products?.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-1 transition-colors duration-200 group ${
                          index === products.length - 1 ? "" : "border-b border-stroke"
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                            #{item.id}
                          </span>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <img
                              src={baseURLFile + item.product_image}
                              alt={item.product_name || "Product image"}
                              className="size-12 rounded-lg border-2 border-white shadow-sm object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/150?text=No+Image";
                              }}
                            />
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.product_name ?? "---"}
                          </h5>
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            {item.section?.section_name ?? "---"}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <span className="text-sm text-body">
                              {item.store?.store_name ?? "---"}
                            </span>
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {item.store?.store_type === "RESTAURANT" ? "Restaurant" :
                             item.store?.store_type === "GROCERIES" ? "Grocery" :
                             item.store?.store_type ?? "---"}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            <Link
                              to={"/products/edit/" + item.id}
                              className="hover:text-primary text-body p-1.5 rounded-full hover:bg-primary/10 transition-colors duration-200"
                              title="Edit Product"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                              </svg>
                            </Link>

                            {item.store?.store_type === "RESTAURANT" && (
                              <Link
                                to={"/products/extras/" + item.id}
                                className="hover:text-blue-600 text-body p-1.5 rounded-full hover:bg-blue-50 transition-colors duration-200"
                                title="Manage Extras"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                                </svg>
                              </Link>
                            )}

                            <button
                              onClick={() => {
                                setEventType("delete");
                                setProductId(item.id);
                                setIsOpen(true);
                              }}
                              className="hover:text-danger text-body p-1.5 rounded-full hover:bg-danger/10 transition-colors duration-200"
                              title="Delete Product"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="mt-6">
                  <Paginate
                    route={"/products?"}
                    search={""}
                    page={page}
                    pages={pages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Enhanced Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this product? This action cannot be undone."
              : "Are you sure?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setProductId("");
            } else if (eventType === "delete" && productId !== "") {
              setLoadEvent(true);
              dispatch(deleteProduct(productId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setProductId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setProductId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default ProductScreen;
